import { useEffect, useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import SignaturePad from "./SignaturePad";
import PhotoInput from "./PhotoInput";
import { useLanguage } from "../i18n/LanguageContext";

const OTRO = "__otro__";

export default function DeliverDialog({ open, pkg, sameUnitOccupants, onClose, onConfirm }) {
  const { t } = useLanguage();
  const [who, setWho] = useState("");
  const [otroNombre, setOtroNombre] = useState("");
  const [obs, setObs] = useState("");
  const [signature, setSignature] = useState(null);
  const [evidencePhotos, setEvidencePhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Reinicia el formulario cada vez que se abre con un paquete distinto
  // (el diálogo permanece montado entre entregas, así que sin esto el
  // formulario arrastraba los datos del paquete anterior).
  useEffect(() => {
    if (open) {
      setWho(pkg?.occupantId?.name || "");
      setOtroNombre("");
      setObs("");
      setSignature(null);
      setEvidencePhotos([]);
      setError("");
    }
  }, [pkg?._id, open]); // eslint-disable-line

  if (!pkg) return null;
  const isOtro = who === OTRO;
  const finalName = isOtro ? otroNombre.trim() : who;
  const packagePhoto = pkg.photos?.[0];

  const handleConfirm = async () => {
    if (!signature || !finalName) {
      setError(!finalName ? t("deliverDialog.errNoWho") : t("deliverDialog.errNoSignature"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onConfirm(pkg._id, {
        deliveredTo: finalName,
        deliveredToType: isOtro ? "otro" : "occupant",
        observations: obs.trim(),
        signature,
        deliveryPhotos: evidencePhotos,
      });
    } catch (err) {
      setError(err.response?.data?.error || t("deliverDialog.genericError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {t("deliverDialog.title")}
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
          {packagePhoto && (
            <Box component="img" src={packagePhoto} sx={{ width: 56, height: 56, objectFit: "cover", borderRadius: 1 }} />
          )}
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {pkg.courier}
              {pkg.courier === "Otro" && pkg.courierNote ? ` (${pkg.courierNote})` : ""} — {pkg.desc}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {pkg.unitId}
            </Typography>
          </Box>
        </Stack>

        <TextField select label={t("deliverDialog.whoReceives")} fullWidth value={who} onChange={(e) => setWho(e.target.value)} sx={{ mb: 2 }}>
          {sameUnitOccupants.map((o) => (
            <MenuItem key={o._id} value={o.name}>
              {o.name} ({o.role})
            </MenuItem>
          ))}
          <MenuItem value={OTRO}>{t("deliverDialog.otherOption")}</MenuItem>
        </TextField>

        {isOtro && (
          <TextField
            label={t("deliverDialog.otherNameLabel")}
            fullWidth
            autoFocus
            value={otroNombre}
            onChange={(e) => setOtroNombre(e.target.value)}
            placeholder={t("deliverDialog.otherNamePlaceholder")}
            sx={{ mb: 2 }}
          />
        )}

        <TextField
          label={t("deliverDialog.observations")}
          fullWidth
          multiline
          minRows={2}
          value={obs}
          onChange={(e) => setObs(e.target.value)}
          placeholder={t("deliverDialog.observationsPlaceholder")}
          sx={{ mb: 2 }}
        />

        <Box sx={{ mb: 2 }}>
          <PhotoInput value={evidencePhotos} onChange={setEvidencePhotos} label={t("deliverDialog.evidencePhotos")} />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
          {t("deliverDialog.signature")}
        </Typography>
        <SignaturePad onChange={setSignature} />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button variant="contained" startIcon={<CheckIcon />} onClick={handleConfirm} disabled={saving}>
          {saving ? t("common.saving") : t("deliverDialog.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
